import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "~/services/shadcn";

const buttonVariants = cva(
  "flex items-center justify-center whitespace-nowrap transition-all duration-200 overflow-visible text-[#F7FAFD] disabled:!cursor-not-allowed disabled:!opacity-50",
  {
    variants: {
      variant: {
        default: "bg-orange-gradient text-black hover:shadow-[0_4px_12px_2px_#382002]",
        outline:
          "relative bg-[url('/button-outline-bg.svg')] text-[#D4D1D1] bg-no-repeat bg-[length:100%_100%] hover:bg-black hover:text-white hover:shadow-[0_2px_14px_6px_#382002] hover:brightness-110",
        ghost: "bg-[#E7C5B71A] font-medium hover:bg-[#E7C5B742]",
        subtle: "bg-[#231d1b]",
        gradient: "bg-button-gradient-3",
        wrapper:
          "flex items-center justify-start rounded-xs bg-white/5 shadow-lg hover:bg-white/10",
        link: "inline-flex hover:underline",
        icon: "!w-6 !h-6 !min-w-[16px] !p-0 hover:bg-[#E7C5B71A] text-[#8c8c8c] hover:text-[#F7FAFD]",
        destructive: "text-black bg-red-500",
        destructive2: "bg-red-500/15 text-red-300 hover:bg-red-500/10",
      },
      size: {
        lg: "h-13 px-8 py-[1px] space-x-3 text-[18px] leading-6 min-w-[168px] font-semibold",
        default:
          "h-11 px-8 py-[1px] space-x-2 text-[15px] leading-6 min-w-[168px] font-semibold decoration-[3px] underline-offset-[10px]",
        sm: "h-9 px-3 py-[1px] space-x-1.5 leading-none !font-semibold text-sm decoration-[2px] underline-offset-[6px]",
        xs: "h-7 px-2 py-[1px] space-x-1.5 leading-none !font-semibold text-xs decoration-[2px] underline-offset-[4px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      isLoading,
      loadingText,
      children,
      asChild = false,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={isLoading || disabled}
        ref={ref}
        {...props}
      >
        {isLoading ? (
          <div className="flex items-center space-x-2">
            {/* TODO: make spinner respect button size */}
            <Loader2 className="h-4 w-4 animate-spin duration-700" />

            {loadingText && <span>{loadingText}</span>}
          </div>
        ) : (
          children
        )}
      </Comp>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
